import React, { useState, useEffect } from "react";
/* MATERIAL COMPONENTS */
import Grid from "@mui/material/Grid";
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

/*DATEPICKER*/
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import "../../assets/css/dashboard_styles.css";
import DashboardImportacaoGrid from "./grid";
import ChartCause from "./charts/ChartCause";
import ChartJustify from "./charts/ChartJustify";
import ChartAmount01 from "./charts/ChartAmount01";
import ChartDate from "./charts/ChartDate";
import { Search } from "@mui/icons-material";
import br from "date-fns/locale/pt-BR";
import DashImportacaoHeaderModel from "../../components/classes/DashImportacaoHeader";
import DashImportDataset from "../../components/data/DashImportDataset";
import ParceiroDataSet from "../../components/data/parceiroDataset";

import { useTranslation } from "react-i18next";

export default function DashboardImportacaoCharts() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en-US"

  // STATES
  const [dataInicio, setDataInicio] = useState<Date>(new Date());
  const [dataFim, setDataFim] = useState<Date>(new Date());
  const [filterControl, setFilterControl] = useState<boolean>(false);
  const [filtroParceiro, setFiltroParceiro] = useState<{ label: string, id: number }[]>([]);
  const [parceiros, setParceiros] = useState<{ label: string, id: number }[]>([]);
  const [dashImportacaoData, setDashImportacaoData] = useState<DashImportacaoHeaderModel>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const dataDashImportacao = new DashImportDataset();
  const parceirosDataSet = new ParceiroDataSet();

  useEffect(() => {
    fetchData();
  }, [])

  useEffect(() => {
    setIsLoading(true);
    dataDashImportacao
      .getAll(
        dataInicio!,
        dataFim!,
        filtroParceiro.map(x => x.id)
      )
      .then((result) => {
        setDashImportacaoData(result);
        setIsLoading(false);
      });
  }, [filterControl]);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      var parceiros = parceirosDataSet.ListarFromCache();
      setParceiros(parceiros.map(x => { return { label: x.apelido + '', id: +x.codigoFornecedorSAP } }));

    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const getQuantidadeRecebida = (): number => {
    return dashImportacaoData?.data?.reduce((somaParcial, item) => somaParcial + item.quantidadeEmbarque, 0) ?? 0;
  }

  const getQuantidadeSolicitada = (): number => {
    return dashImportacaoData?.data?.reduce((somaParcial, item) => somaParcial + item.quantidadePedido, 0) ?? 0;
  }

  const getPeriodo = (): String => {
    var groupedByDay = dashImportacaoData?.data?.reduce(function (r, a) {
      r[new Date(a.eta).toUTCString()] = r[new Date(a.eta).toUTCString()] || [];
      r[new Date(a.eta).toUTCString()].push(a);
      return r;
    }, Object.create(null)) ?? [];
    var sortedDays = Object.keys(groupedByDay).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    return `${new Date(sortedDays[0]).toLocaleDateString()} - ${new Date(sortedDays[sortedDays.length - 1]).toLocaleDateString()}`;
  }

  const getQuantidadeAReceber = () => {
    return dashImportacaoData?.statusCount?.map(status => {
      return (
        <Box className="dashboard-box__item" mb={2}>
          <Typography
            variant="subtitle2"
            fontSize="small"
            sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
          >
            {!status.key || status.key === null ? t("pages.dashboardImport.text.withoutStatus") : status.key}
          </Typography>
          <div className="h-rule"></div>

          <Typography variant="subtitle2" pt={1} color="#4c4c4c">
            {status.value}
          </Typography>
        </Box>
      )
    })
  }

  return (
    <>
      {isLoading ?
        (
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
            className="dashboard-container">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="dashboard-content">
              <CircularProgress size={48} />
              <small style={{ marginTop: 10 }}>{t("pages.dashboardImport.text.loadingDataPleaseWait")}</small>
            </div>
          </div>
        ) : (
          <div className="dashboard-container">
            <div className="dashboard-content">
              <Grid
                container
                spacing="16"
                className="dashboard-header"
                sx={{ marginTop: "0" }}
              >
                <Grid item xs={12} lg={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={br}>
                    <div
                      style={{
                        width: "100%",
                        height: "6em",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ marginRight: 10 }}>
                        <DatePicker
                          label={t("pages.dashboardImport.text.startDate")}
                          value={dataInicio}
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setDataInicio(newValue);
                            }
                          }}
                          renderInput={(params: any) => <TextField {...params} />}
                        />
                      </div>
                      <div>
                        <DatePicker
                          label={t("pages.dashboardImport.text.finalDate")}
                          value={dataFim}
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setDataFim(newValue);
                            }
                          }}
                          renderInput={(params: any) => <TextField {...params} />}
                        />
                      </div>
                    </div>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <div
                    style={{
                      width: "100%",
                      height: "6em",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Autocomplete
                      disablePortal
                      id="parceiros"
                      options={parceiros}
                      value={filtroParceiro}
                      multiple={true}
                      onChange={(e: any, newValue: any | null) => {
                        setFiltroParceiro(newValue ?? []);
                      }}
                      renderInput={(params) => <TextField {...params} label={t("pages.dashboardImport.text.partners")} />}
                      style={{ zIndex: 1000 }}
                      sx={{ width: '100%' }}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} lg={2}>
                  <div
                    style={{
                      width: "100%",
                      height: "6em",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title={t("pages.dashboardImport.text.updateDashboardWithSelectedFilters")}>
                      <span>
                        <IconButton onClick={() => setFilterControl(!filterControl)} style={{ borderRadius: "0" }}>
                          <Search />
                          <Typography variant="subtitle2">{t("pages.dashboardImport.text.applyFilters")}</Typography>
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </Grid>
              </Grid>

              <Grid container spacing="16" className="dashboard-charts" mt={0}>
                <Grid item xs={12} lg={4}>
                  <Box>
                    <Card className="dashboard-header__card">
                      <Typography variant="subtitle2">
                        {t("pages.dashboardImport.text.deviationsDividedByCause")}
                      </Typography>
                      <div className="h-rule"></div>
                      <Stack mt={1}>
                        <ChartCause data={dashImportacaoData?.data} />
                      </Stack>
                    </Card>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Box>
                    <Card className="dashboard-header__card">
                      <Typography variant="subtitle2">
                        {t("pages.dashboardImport.text.deviationsDividedByJustification")}
                      </Typography>
                      <div className="h-rule"></div>
                      <Stack mt={1}>
                        <ChartJustify data={dashImportacaoData?.data} />
                      </Stack>
                    </Card>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Box>
                    <Card
                      className="dashboard-header__card"
                      sx={{ minHeight: "300px" }}
                    >
                      <Typography variant="subtitle2">{t("pages.dashboardImport.text.adherence")}</Typography>
                      <div className="h-rule"></div>

                      <Box
                        className="dashboard-flexbox"
                        pt={2}
                        sx={{ justifyContent: "center" }}
                      >
                        <Box
                          className="dashboard-box__item"
                          sx={{ minHeight: "78px" }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontSize="small"
                            sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                          >
                            {isEnglish ? (
                              <>
                                {t("pages.dashboardImport.text.staticDate")} <br /> {t("pages.dashboardImport.text.quantity")}
                              </>
                            ) : (
                              <>
                                {t("pages.dashboardImport.text.quantity")} <br /> {t("pages.dashboardImport.text.staticDate")}
                              </>
                            )}
                          </Typography>
                          <div className="h-rule"></div>
                          <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                            {dashImportacaoData?.aderencia.quantidadeDataEstatica}
                          </Typography>
                        </Box>
                        <Box
                          className="dashboard-box__item"
                          sx={{ minHeight: "78px" }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontSize="small"
                            sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                          >
                            {isEnglish ? (
                              <>
                                {t("pages.dashboardImport.text.shippingDate")} <br /> {t("pages.dashboardImport.text.quantity")}
                              </>
                            ) : (
                              <>
                                {t("pages.dashboardImport.text.quantity")} <br /> {t("pages.dashboardImport.text.shippingDate")}
                              </>
                            )}
                          </Typography>
                          <div className="h-rule"></div>
                          <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                            {dashImportacaoData?.aderencia.quantidadeDataRemessa}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack>
                        <ChartDate aderencia={dashImportacaoData?.aderencia} />
                      </Stack>
                    </Card>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Card className="dashboard-chart__card" sx={{ display: "flex" }}>
                    <div className="chart-box" style={{ width: "50%" }}>
                      <Box>
                        <Typography variant="subtitle2">
                          {t("pages.dashboardImport.text.requestVersusReceiving")}
                        </Typography>
                        <div className="h-rule"></div>
                      </Box>
                      <Box
                        className="dashboard-flexbox"
                        pt={2}
                        sx={{ justifyContent: "center", alignItems: "flex-start" }}
                      >
                        <Box
                          className="dashboard-box__item"
                          mb={2}
                          sx={{
                            minHeight: "78px",
                            maxHeight: "192px",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontSize="small"
                            mt={2}
                            sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                          >
                            {t("pages.dashboardImport.text.period")}
                          </Typography>
                          <div className="h-rule"></div>
                          <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                            {getPeriodo()}
                          </Typography>
                        </Box>
                        <Box
                          className="dashboard-box__item"
                          mb={2}
                          sx={{ minHeight: "78px" }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontSize="small"
                            sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                          >
                            {isEnglish ? (
                              <>
                                {t("pages.dashboardImport.text.requested")} <br /> {t("pages.dashboardImport.text.quantity")}
                              </>
                            ) : (
                              <>
                                {t("pages.dashboardImport.text.quantity")} <br /> {t("pages.dashboardImport.text.requested")}
                              </>
                            )}
                          </Typography>
                          <div className="h-rule"></div>
                          <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                            {getQuantidadeSolicitada()}
                          </Typography>
                        </Box>
                        <Box
                          mb={2}
                          className="dashboard-box__item"
                          sx={{ minHeight: "78px" }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontSize="small"
                            sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                          >
                            {isEnglish ? (
                              <>
                                {t("pages.dashboardImport.text.received")} <br /> {t("pages.dashboardImport.text.quantity")}
                              </>
                            ) : (
                              <>
                                {t("pages.dashboardImport.text.quantity")} <br /> {t("pages.dashboardImport.text.received")}
                              </>
                            )}
                          </Typography>
                          <div className="h-rule"></div>
                          <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                            {getQuantidadeRecebida()}
                          </Typography>
                        </Box>
                      </Box>
                    </div>
                    <div
                      className="chart-box"
                      style={{ margin: "auto", width: "50%", paddingTop: "8px" }}
                    >
                      <ChartAmount01 data={dashImportacaoData?.data} />
                    </div>
                  </Card>
                </Grid>
              </Grid>

              <Grid mt={2}>
                <Card className="dashboard-chart__card">
                  <Grid container spacing="16" className="dashboard-charts" mt={0}>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="subtitle2">
                          {t("pages.dashboardImport.text.amountToReceive")}
                        </Typography>
                        <div className="h-rule"></div>
                      </Box>
                      <Box
                        className="dashboard-flexbox"
                        pt={2}
                        sx={{ justifyContent: "center", alignItems: "flex-start" }}
                      >
                      </Box>
                      <Box
                        className="dashboard-flexbox"
                        pt={2}
                        sx={{ justifyContent: "flex-start", flexWrap: "nowrap", overflowX: "scroll" }}
                      >
                        {getQuantidadeAReceber()}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid mt={2}>
                <DashboardImportacaoGrid dashImportacaoArr={dashImportacaoData} />
              </Grid>
            </div>
          </div>
        )}
    </>
  );
}